import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, BarChart3, Users, CreditCard, Settings, Search, Bell } from "lucide-react";
import ScrollCue from "@/components/ScrollCue";
import { subscribeScroll, prefersReducedMotion } from "@/lib/scrollEngine";

const terminalLines = [
  { text: "> devcore init acme-platform", type: "cmd" },
  { text: "✓ workspace scaffolded", type: "ok" },
  { text: "> drafting design system…", type: "cmd" },
  { text: "✓ tokens · typography · grid", type: "ok" },
  { text: "> generating UI layout", type: "cmd" },
  { text: "✓ responsive · accessible · themed", type: "ok" },
  { text: "> installing modules…", type: "cmd" },
  { text: "✓ auth · billing · database", type: "ok" },
  { text: "> wiring API & business logic", type: "cmd" },
  { text: "✓ endpoints · validation · jobs", type: "ok" },
  { text: "> building UI components", type: "cmd" },
  { text: "✓ dashboard · charts · tables", type: "ok" },
  { text: "> hardening security…", type: "cmd" },
  { text: "✓ RLS · encryption · audit log", type: "ok" },
  { text: "> running tests & CI", type: "cmd" },
  { text: "✓ 142 passed · 0 failed", type: "ok" },
  { text: "> deploying to the edge…", type: "cmd" },
  { text: "✓ live at acme.app", type: "ok" },
] as const;



const ProcessSection = () => {
  const { t } = useTranslation();
  const stages = t("process.stages", { returnObjects: true }) as string[];
  const outerRef = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);


  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      setP(1);
      return;
    }
    return subscribeScroll(() => {
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setP(total > 0 ? scrolled / total : 0);
    });
  }, []);

  // how many terminal lines are visible
  const linesVisible = Math.round(p * (terminalLines.length + 1));
  // dashboard assembly stages
  const showFrame = p > 0.14;
  const showSidebar = p > 0.32;
  const showTopbar = p > 0.42;
  const showChart = p > 0.55;
  const showCards = p > 0.66;
  const deployed = p > 0.82;
  const activeStage = Math.min(stages.length - 1, Math.floor(p * stages.length));

  // counter motion — terminal auto-scrolls so the newest lines stay visible
  const lineH = isMobile ? 21 : 23; // approx px per line
  const maxVisibleLines = isMobile ? 4 : 11;
  const termShift = -Math.max(0, linesVisible - maxVisibleLines) * lineH;


  const dashShift = (1 - Math.min(p / 0.5, 1)) * 60; // dashboard slides in from right

  const chartBars = [38, 62, 48, 80, 56, 72, 90];

  return (
    <section id="process" ref={outerRef} className="relative bg-secondary/50" style={{ height: "260vh" }}>
      <div className="sticky top-0 h-[100dvh] flex flex-col justify-center overflow-hidden pt-16 md:pt-20">
        <div className="absolute inset-0 tech-grid opacity-[0.12] pointer-events-none" />
        <div className="container relative">
          <div className="text-center max-w-2xl mx-auto mb-4 md:mb-12">
            <p className="mono-label mb-2 md:mb-3">// how_we_work</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              {t("process.title")}
            </h2>
          </div>

          {/* Stage indicator */}
          <div className="flex items-center justify-center gap-2 md:gap-3 mb-4 md:mb-20 flex-wrap">
            {stages.map((s, i) => (
              <div key={s} className="flex items-center gap-2 md:gap-3">
                <span
                  className={`font-mono text-[11px] md:text-xs tracking-[0.15em] uppercase transition-colors duration-500 ${
                    i <= activeStage ? "text-accent" : "text-muted-foreground/40"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")} {s}
                </span>
                {i < stages.length - 1 && (
                  <span className="h-px w-5 md:w-8 bg-border relative overflow-hidden">
                    <span
                      className="absolute inset-y-0 left-0 bg-accent transition-[width] duration-500"
                      style={{ width: i < activeStage ? "100%" : "0%" }}
                    />
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4 lg:gap-8 items-stretch max-w-6xl mx-auto">
            {/* LEFT — terminal */}
            <div
              className="relative rounded-2xl border border-border bg-background/80 backdrop-blur-sm overflow-hidden shadow-2xl min-h-[170px] md:min-h-[340px]"
            >
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card/60">
                <span className="w-3 h-3 rounded-full bg-destructive/70" />
                <span className="w-3 h-3 rounded-full bg-accent/50" />
                <span className="w-3 h-3 rounded-full bg-primary/70" />
                <span className="ml-3 font-mono text-[11px] text-muted-foreground">devcore - zsh</span>
              </div>
              <div className="p-5 md:p-6 font-mono text-[13px] md:text-sm leading-relaxed h-[120px] md:h-[280px] overflow-hidden">
                <div style={{ transform: `translateY(${termShift}px)`, transition: "transform 120ms linear" }}>
                  {terminalLines.map((line, i) => {
                    const visible = i < linesVisible;
                    const isLast = i === linesVisible - 1;
                    return (
                      <div
                        key={i}
                        className={`transition-[opacity,transform] duration-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"} ${
                          line.type === "ok" ? "text-accent" : "text-foreground"
                        }`}
                      >
                        {visible ? line.text : ""}
                        {isLast && visible && <span className="terminal-cursor" />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* RIGHT — assembling SaaS dashboard */}
            <div
              className="relative rounded-2xl border border-border bg-card overflow-hidden shadow-2xl min-h-[260px] md:min-h-[340px]"
              style={{
                transform: `translateX(${dashShift}px)`,
                opacity: showFrame ? 1 : 0.25,
                transition: "transform 200ms cubic-bezier(0.16,1,0.3,1), opacity 400ms ease",
              }}
            >
              {/* skeleton wash before frame */}
              {!showFrame && <div className="absolute inset-0 tech-dots opacity-20" />}

              <div className="flex h-[260px] md:h-[340px]">
                {/* sidebar */}
                <div
                  className="w-14 md:w-16 border-r border-border bg-background/60 flex flex-col items-center gap-4 py-4 transition-[transform,opacity] duration-500"
                  style={{
                    transform: showSidebar ? "translateX(0)" : "translateX(-100%)",
                    opacity: showSidebar ? 1 : 0,
                  }}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                    <LayoutDashboard size={16} className="text-accent" />
                  </div>
                  {[BarChart3, Users, CreditCard, Settings].map((Ic, i) => (
                    <div key={i} className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center">
                      <Ic size={15} className="text-muted-foreground" />
                    </div>
                  ))}
                </div>

                <div className="flex-1 flex flex-col">
                  {/* topbar */}
                  <div
                    className="flex items-center justify-between px-4 py-3 border-b border-border transition-[transform,opacity] duration-500"
                    style={{ transform: showTopbar ? "translateY(0)" : "translateY(-100%)", opacity: showTopbar ? 1 : 0 }}
                  >
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Search size={14} />
                      <span className="font-mono text-[11px]">search…</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Bell size={14} className="text-muted-foreground" />
                      <span className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-accent" />
                    </div>
                  </div>

                  <div className="flex-1 p-4 space-y-4">
                    {/* stat cards */}
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { v: "12.4k", l: "Users" },
                        { v: "$48k", l: "MRR" },
                        { v: "99.9%", l: "Uptime" },
                      ].map((c, i) => (
                        <div
                          key={c.l}
                          className="rounded-xl border border-border bg-background/60 p-3 transition-[transform,opacity] duration-500"
                          style={{
                            transform: showCards ? "translateY(0) scale(1)" : "translateY(12px) scale(0.96)",
                            opacity: showCards ? 1 : 0,
                            transitionDelay: `${i * 80}ms`,
                          }}
                        >
                          <p className="text-base md:text-lg font-bold text-foreground tabular-nums">{c.v}</p>
                          <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{c.l}</p>
                        </div>
                      ))}
                    </div>

                    {/* chart */}
                    <div className="rounded-xl border border-border bg-background/60 p-4">
                      <div className="flex items-end justify-between gap-2 h-24">
                        {chartBars.map((h, i) => (
                          <div
                            key={i}
                            className="flex-1 rounded-t bg-gradient-to-t from-primary to-accent transition-[height,opacity] duration-700"
                            style={{
                              height: showChart ? `${h}%` : "0%",
                              opacity: showChart ? 1 : 0,
                              transitionDelay: `${i * 70}ms`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* finished badge */}
              <div
                className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-success text-success-foreground transition-[transform,opacity] duration-500"
                style={{ transform: deployed ? "translateY(0) scale(1)" : "translateY(20px) scale(0.8)", opacity: deployed ? 1 : 0 }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-success-foreground animate-pulse" />
                <span className="font-mono text-[11px] tracking-wide">finished</span>
              </div>
            </div>
          </div>
        </div>
        <ScrollCue progress={p} />
      </div>
    </section>
  );
};

export default ProcessSection;
